import { useLatest } from '@qfa/react-components';
import { refetchLocationsListQuery, useSaveLocationMutation } from '@kinetic-ui/data-layer';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { LocationFormValues } from '../types';
import { useLocationFormFields } from './useLocationFormFields';
import { useGraphLoading, useRouteBuilder, useSaveHotKey } from 'kineticUtils';
import { LOCATIONS_PAGE_ROUTE } from 'const';
import { PageHeaderProps } from 'components/layouts';
import { LocationFormToInput } from '../utils/formToInput';

export const useLocationForm = (initialData: LocationFormValues, hasPrivilege: boolean) => {
  const { t } = useTranslation(['locations', 'common']);
  const routeBuilder = useRouteBuilder(LOCATIONS_PAGE_ROUTE);
  const { push } = useHistory();

  const {
    hasErrors,
    valuesProps: { values },
    setDirtyFields,
    saveButtonProps: { isSaveDisabled: isSaveButtonDisabled },
    valuesProps: { setValues },
  } = useLocationFormFields({ initialData, hasPrivilege });

  const isValidRef = useLatest(!hasErrors);

  const headerProps: PageHeaderProps = useMemo(() => {
    return {
      title: values.name ?? t('locations:locationNamePlaceholder'),
    };
  }, [values.name, t]);

  const [saveLocation, result] = useSaveLocationMutation({
    refetchQueries: [refetchLocationsListQuery()],
  });
  useGraphLoading({
    result,
    name: 'saveLocation',
    messages: {
      success: t('savedLocationSuccess'),
      error: t('saveLocationError'),
    },
  });
  const valuesRef = useLatest(values);

  const onSaveClick = useCallback(() => {
    if (!isValidRef.current) {
      return;
    }
    const locationInput = LocationFormToInput(valuesRef?.current);

    return saveLocation({
      variables: {
        input: {
          ...locationInput,
        },
      },
      refetchQueries: [refetchLocationsListQuery()],
    }).then(({ data }) => {
      if (data) {
        setDirtyFields([]);
        /*
        In Signals, there is a reset(intersectionToForm(toIntersectionInput(data.saveIntersection))); call
        This doesn't seem to be needed here, but leaving a hint here in case there is weird post-create
        behavior discovered in the future     values: [values, setValues, reset],
         */
        setTimeout(() => {
          if (!values.id && data.saveLocation?.id) {
            push(routeBuilder(`edit/${data.saveLocation?.id}`));
          }
        }, 100);
      }
    });
  }, [isValidRef, push, routeBuilder, saveLocation, setDirtyFields, values.id, valuesRef]);

  useSaveHotKey(isSaveButtonDisabled, onSaveClick);

  const returnData = useMemo(
    () => ({
      headerProps,
      valuesProps: {
        values,
        setValues,
      },
      hasErrors,
      saveButtonProps: {
        onSaveClick,
        isSaveDisabled: isSaveButtonDisabled,
      },
    }),
    [headerProps, values, setValues, hasErrors, onSaveClick, isSaveButtonDisabled]
  );

  return returnData;
};
